"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { MapRouteProps } from "../../../types/props/map-route-props";
import { addEndpointMarkers } from "../../../utils/add-markers-leaflet";
import {
  decodePolyline,
  formatDistance,
  formatDuration,
} from "../../../utils/decode-polyline";
import { Adresse } from "../../../types/models/adresse";
import { RouteInfo } from "../../../types/interfaces/route-info";
import { useKmsService } from "../../../providers/kms-service-provider";
import { cn } from "@onclicks/sharedcomponents/ui";
import { formatAdresse, isRoutableTrip } from "../../../utils/course-stops";

const colorLine = "#3b82f6";

type RouteData = {
  points: [number, number][];
  adresses: Adresse[];
};

export const MapRoute = ({ adresses }: MapRouteProps) => {
  const { adresseService } = useKmsService();
  const t = useTranslations("map_preview");

  const miniContainerRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const miniMapRef = useRef<any>(null);
  const modalMapRef = useRef<any>(null);
  const polylineRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);

  const [mapReady, setMapReady] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null);
  const [routeData, setRouteData] = useState<RouteData | null>(null);

  const addressKey = JSON.stringify(adresses);

  // 1) Initialiser la mini map UNE SEULE FOIS
  useEffect(() => {
    if (!miniContainerRef.current || miniMapRef.current) return;

    let cancelled = false;

    async function initMap() {
      const L = (await import("leaflet")).default;
      // @ts-ignore
      await import("leaflet/dist/leaflet.css");

      if (cancelled || !miniContainerRef.current) return;

      const map = L.map(miniContainerRef.current, {
        zoomControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        attributionControl: false,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
        map,
      );

      miniMapRef.current = { map, L };
      setMapReady(true); // <-- Déclenche le second useEffect
    }

    initMap();

    return () => {
      cancelled = true;
    };
  }, []);

  // 2) Mettre à jour l'itinéraire quand la map est prête OU quand les adresses changent
  useEffect(() => {
    if (!mapReady || !miniMapRef.current) return;

    let cancelled = false;
    const { map, L } = miniMapRef.current;

    // Nettoyer l'ancien tracé et les anciens marqueurs
    if (polylineRef.current) {
      map.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }
    markersRef.current.forEach((marker) => map.removeLayer(marker));
    markersRef.current = [];

    setError(null);
    setRouteInfo(null);
    setRouteData(null);

    // Garde-fou : sans deux adresses routables le backend n'a rien à calculer.
    // Les appelants ne montent en principe pas la carte dans ce cas, mais un
    // arrêt vidé en cours de saisie ne doit pas repartir en requête.
    if (!isRoutableTrip(adresses.map((adresse) => ({ adresse })))) {
      setLoading(false);
      return;
    }

    setLoading(true);

    async function updateRoute() {
      try {
        const route = await adresseService.calculerItineraire(adresses);

        if (cancelled) return;

        // La polyline encodée Google (précision 5) se décode déjà en [lat, lng]
        // (ordre Leaflet) : aucun swap.
        const points = decodePolyline(route.encodedPolyline);

        setRouteInfo({
          distance: formatDistance(route.distanceText, route.distanceMeters),
          duration: formatDuration(route.durationText, route.durationSeconds),
        });

        setRouteData({ points, adresses });

        const polyline = L.polyline(points, {
          color: colorLine,
          weight: 3,
        }).addTo(map);

        polylineRef.current = polyline;

        const markers = addEndpointMarkers(
          L,
          map,
          points,
          formatAdresse(adresses[0]!),
          formatAdresse(adresses[adresses.length - 1]!),
        );
        markersRef.current = markers;

        map.fitBounds(polyline.getBounds(), {
          padding: [20, 20],
          maxZoom: 15,
        });
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message ?? t("error_fallback"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    updateRoute();

    return () => {
      cancelled = true;
    };
  }, [mapReady, addressKey]); // <-- Réagit quand la map est prête ET quand les adresses changent

  // 3) Modal map
  useEffect(() => {
    if (!isExpanded || !modalContainerRef.current || !routeData) return;

    let cancelled = false;

    const initModal = async () => {
      const L = (await import("leaflet")).default;

      if (cancelled || !modalContainerRef.current) return;

      const map = L.map(modalContainerRef.current, {
        zoomControl: true,
        attributionControl: true,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
        map,
      );

      modalMapRef.current = { map, L };

      const { points, adresses: addrs } = routeData;

      const polyline = L.polyline(points, {
        color: colorLine,
        weight: 4,
      }).addTo(map);

      addEndpointMarkers(
        L,
        map,
        points,
        formatAdresse(addrs[0]!),
        formatAdresse(addrs[addrs.length - 1]!),
      );
      map.fitBounds(polyline.getBounds(), { padding: [40, 40], maxZoom: 15 });
      // La carte est montée dans une modale : recalculer la taille pour éviter
      // un rendu partiel des tuiles.
      map.invalidateSize();
    };

    initModal();

    return () => {
      cancelled = true;
      if (modalMapRef.current) {
        modalMapRef.current.map.remove();
        modalMapRef.current = null;
      }
    };
  }, [isExpanded, routeData]);

  // 4) Cleanup global au démontage
  useEffect(() => {
    return () => {
      if (miniMapRef.current) {
        miniMapRef.current.map.remove();
        miniMapRef.current = null;
      }
      if (modalMapRef.current) {
        modalMapRef.current.map.remove();
        modalMapRef.current = null;
      }
    };
  }, []);

  const modal =
    isExpanded &&
    typeof window !== "undefined" &&
    createPortal(
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={() => setIsExpanded(false)}
      >
        <div
          className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="h-[500px] w-full">
            <div ref={modalContainerRef} className="w-full h-full" />
          </div>
          <div className="flex items-center justify-between gap-4 p-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex flex-col gap-1.5 text-slate-600 dark:text-slate-400 text-sm">
              {adresses.map((address, index) => {
                const isFirst = index === 0;
                const isLast = index === adresses.length - 1;
                return (
                  <div key={index} className="flex items-center gap-2">
                    <span
                      className={cn(
                        "w-2.5 h-2.5 rounded-full shrink-0 bg-amber-400",
                        isFirst && "bg-green-500",
                        isLast && "bg-red-500",
                      )}
                    />
                    {isFirst
                      ? t("departure_label")
                      : isLast
                        ? t("arrival_label")
                        : t("stop_label", { n: index })}{" "}
                    {formatAdresse(address)}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              {routeInfo && (
                <div className="flex gap-2">
                  <span className="text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full text-slate-600 dark:text-slate-300">
                    🚗 {routeInfo.distance}
                  </span>
                  <span className="text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full text-slate-600 dark:text-slate-300">
                    ⏱ {routeInfo.duration}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <div
        className="relative w-full h-full rounded-xl overflow-hidden cursor-pointer group"
        onClick={() => !loading && !error && setIsExpanded(true)}
      >
        <div ref={miniContainerRef} className="w-full h-full" />

        {loading && (
          <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800 flex flex-col items-center justify-center gap-2">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-slate-400">{t("calculating")}</span>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 bg-red-50 flex items-center justify-center p-3">
            <p className="text-red-500 text-xs text-center">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-end justify-center pb-2">
            <span className="text-white text-[10px] font-medium bg-black/40 px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              {t("expand_hint")}
            </span>
          </div>
        )}
      </div>
      {modal}
    </>
  );
};
